import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Info } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAdminCustomization } from '@/hooks/useAdminCustomization';

export const PerformanceScoreExplanation: React.FC = () => {
  const { t } = useLanguage();
  const { customization } = useAdminCustomization();
  const app_name = customization?.app_name;
  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-600" />
          {t("hundredPointSystem") || "100-Point Performance Evaluation System"}
        </CardTitle>
        <CardDescription>
          {t("understandingNewSystem") || "Understanding the new ideal grading system"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Base Score Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("baseScoreFrom")?.replace('{appName}', app_name || 'OVIT') || `Base Score from ${app_name || 'OVIT'}`}</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">80% {t("ofPercentage")?.replace('{appName}', app_name || 'OVIT') || `of ${app_name || 'OVIT'} percentage:`}</span>
                <Badge variant="outline">0-80 {t("points") || "points"}</Badge>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              {t("baseScoreCalculated")?.replace('{appName}', app_name || 'OVIT') || `Base score is calculated as 80% of the average score of ${app_name || 'OVIT'} evaluations.`}
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("operationalActivityMax10") || "Operational Activity (max 10 points)"}</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t("emergencyReports") || "Emergency reports:"}:</span>
                <span className="text-green-600">3 {t("pointsPerReport") || "points/report"}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("patrols") || "Patrols:"}:</span>
                <span className="text-green-600">2 {t("pointsPerPatrol") || "points/patrol"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("supervisorReportsMax") || "Supervisor Reports (max ±10 points)"}</h3>
            <p className="text-xs text-gray-600">
              {t("basedOnPositivityNegativity") || "Based on positivity or negativity of reports from supervisors."}
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("otherCategories") || "Other Categories"}</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t("clientEvaluations") || "Client evaluations"}:</span>
                <span className="text-blue-600">{t("upTo5Points") || "up to ±5 points"}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("behaviors") || "Behaviors"}:</span>
                <span className="text-blue-600">{t("upTo15Points") || "up to ±15 points"}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("punctuality") || "Punctuality"}:</span>
                <span className="text-green-600">{t("upTo5Points") || "up to ±5 points"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Grade Scale */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">{t('gradingScale') || 'Κλίμακα Βαθμολογίας'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800">A+</Badge>
              <span className="text-sm">95+ {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800">A</Badge>
              <span className="text-sm">90-94 {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-800">B+</Badge>
              <span className="text-sm">85-89 {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-800">B</Badge>
              <span className="text-sm">80-84 {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-100 text-yellow-800">C+</Badge>
              <span className="text-sm">75-79 {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-100 text-yellow-800">C</Badge>
              <span className="text-sm">70-74 {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-100 text-orange-800">D</Badge>
              <span className="text-sm">60-69 {t('points') || 'πόντοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-800">F</Badge>
              <span className="text-sm">&lt;60 {t('points') || 'πόντοι'}</span>
            </div>
          </div>
        </div>

        {/* Distinction Badge */}
        <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
          <h3 className="font-semibold text-lg text-amber-800 mb-2">
            🏆 {t("distinctionBadge") || "Distinction Badge"} (100+ {t("points") || "points"})
          </h3>
          <p className="text-sm text-amber-700">
            {t("distinctionBadgeDescription") || "Employees who achieve a score above 100 earn the distinction badge and appear as '100+ (A+)' on their profile, indicating exceptional performance."}
          </p>
        </div>

        {/* How Points are Calculated */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">{t('pointsCalculation') || 'Υπολογισμός Πόντων'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium text-green-700">{t("bonusPointsPositiveOnlyShort") || "Bonus Points (Positive Only)"}</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• {t("patrolsPointsRange") || "Patrols: 1-5 points depending on number"}</li>
                <li>• {t("emergencyReportsPointsRange") || "Emergency reports: 1 point per report (up to 3)"}</li>
                <li>• {t("punctualityBonusForPerfect") || "Punctuality: Bonus for perfect attendance"}</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-blue-700">{t("performancePoints") || "Performance Points (±)"}</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• {t("supervisorReportsPositivityNegativity") || "Supervisor reports: Based on positivity/negativity"}</li>
                <li>• {t("clientEvaluationsBasedOnRating") || "Client evaluations: Based on rating 1-5"}</li>
                <li>• {t("behaviorsPositiveNegative") || "Behaviors: Positive/negative behaviors"}</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};